import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API, { clearAuthTokens } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await API.get('/auth/me');
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { checkAuth(); }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await API.post('/auth/login', { email, password });
        // Explicitly store tokens (interceptor may not catch on all browsers)
        if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
        }
        if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
        }
        setUser(data);
        return data;
    };

    const register = async (name, email, password, role) => {
        const { data } = await API.post('/auth/register', { name, email, password, role });
        if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
        }
        if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
        }
        setUser(data);
        return data;
    };

    const logout = async () => {
        try { await API.post('/auth/logout'); } catch {}
        clearAuthTokens();
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
